import { useEffect } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CircularProgress } from "@mui/material";
import { useFeatureFlag } from "../../hooks/useFeatureFlags";
import useAuthentication from "../../hooks/useAuthentication";
import {
  frontendRoutes,
  LANDING_STAY_QUERY_PARAM,
} from "../../utility/frontendRoutes";
import LandingLayout from "./LandingLayout";
import Hero from "./components/Hero";
import FeatureGrid from "./components/FeatureGrid";
import BuyerSellerSteps from "./components/BuyerSellerSteps";
import TestimonialWall from "./components/TestimonialWall";
import FaqSection from "./components/FaqSection";
import GetTheAppSection from "./components/GetTheAppSection";

const LandingPage = () => {
  const showTestimonials = useFeatureFlag("landing-testimonials");
  const { isLoggedIn, isLoggingIn, isInitializing } = useAuthentication();
  const [searchParams] = useSearchParams();
  const stay = searchParams.has(LANDING_STAY_QUERY_PARAM);

  // On a cold load with a hash in the URL (e.g. a deep link to #features),
  // the browser tries to scroll before the section has mounted. Retry once
  // content is in the DOM.
  useEffect(() => {
    if (!window.location.hash) return;
    const id = window.location.hash.slice(1);
    const el = document.getElementById(id);
    el?.scrollIntoView();
  }, [showTestimonials]);

  // The favicon swap now lives in LandingLayout, so every page wearing the
  // landing chrome gets it, not just this one.

  // While the persisted-session check is still in flight, hold off on
  // rendering the marketing page — otherwise a logged-in visitor briefly
  // sees the full landing page flash before the redirect below fires.
  // Mirrors the ProtectedRoute/GuestRoute pattern. `stay` (AppFooter's
  // "back to landing" link) always bypasses this so that link works
  // instantly regardless of auth state.
  if (!stay && (isInitializing || isLoggingIn)) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress size={40} />
      </div>
    );
  }

  // Redirect a logged-in visitor straight to the feed — the marketing page
  // has already done its job for them.
  if (!stay && isLoggedIn) {
    return <Navigate to={frontendRoutes.HOME} replace />;
  }

  return (
    <LandingLayout>
      <Helmet>
        <title>Ngwaahia | Buy & Sell With Local Sellers</title>
        <meta
          name="description"
          content="Ngwaahia is a marketplace for discovering products from local sellers and creating your own online shop. Buy and sell with ease, trust, and transparency."
        />
        <link rel="canonical" href="https://ngwaahia.com/" />
      </Helmet>
      <Hero />
      <FeatureGrid />
      <BuyerSellerSteps />
      <FaqSection />
      {showTestimonials && <TestimonialWall />}
      <GetTheAppSection />
    </LandingLayout>
  );
};

export default LandingPage;
